import { extractQuestionCitationIndices, QuestionText } from 'utils/questionMarkdown';
import { CitationList } from 'components/Citations/CitationList';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, BookOpen, CheckCircle2, ChevronDown, ChevronUp, Home, RefreshCcw, ShieldAlert, Trophy, XCircle } from 'lucide-react';

import { useSupportEmail } from 'hooks/useSupportEmail';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Progress } from 'components/ui/progress';
import { useEventTracking } from 'hooks/useEventTracking';
import { courses, quiz } from '../../services/api';
import { Flow, QuestionResult, QuizResults as QuizResultsType } from '../../types';
import { useAuth } from 'contexts/AuthContext';
import { useThemeCompletionEvents } from 'components/QuestionFlow/themes/themeCompletionEvents';
import { useFlowThemeTransitionReady } from 'components/QuestionFlow/themes/FlowThemeTransition';
import { getThemeVisualSeed } from 'components/QuestionFlow/themes/useLearnerThemeContext';
import { useDynamicThemeContext, ThemeContextProvider, type ThemeContextInput } from 'components/QuestionFlow/themes';
import { buildFlowPath } from '../../utils/flowNavigation';
import {
  FlowThemeLayer,
  FlowThemeNavBand,
  resolveFlowTheme,
  type FlowThemeProgress,
} from 'components/QuestionFlow/themes';

type ThemeModule = Pick<Flow, 'key' | 'name' | 'title' | 'estimated_duration_minutes' | 'theme_content'>;
interface LocationState {
  themeModule?: ThemeModule;
  themeCompletionId?: string;
  quizResults?: QuizResultsType;
  flowName?: string;
  courseId?: number | null;
  /** The quiz's effective theme, handed over by the quiz so the results continue it. */
  flowTheme?: string | null;
  /** How many steps the quiz's theme walked through. */
  themeTotal?: number;
}

interface QuizCourseInfo {
  course: any;
  flow: any;
  nextFlowName: string | null;
  nextFlowTitle?: string | null;
  chapterTitle?: string | null;
}

const toRoundedNumber = (value: number | string | undefined | null, fallback: number) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.round(parsed);
    }
  }

  return fallback;
};

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleString();
};

const formatAnswerValue = (value: QuestionResult['user_answer'] | QuestionResult['correct_answer']) => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const isPassOnlyResults = (results: QuizResultsType | null | undefined) =>
  Boolean(results?.is_pass_only) || results?.passing_score === null;

const QuizResults: React.FC = () => {
  const supportEmail = useSupportEmail();
  const cardVariant = 'whiteGlass' as const;

  const { flowId } = useParams<{ flowId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useEventTracking();
  const { user } = useAuth();
  const [visualSeed] = useState(getThemeVisualSeed);
  const [searchParams] = useSearchParams();
  const state = (location.state as LocationState | null) ?? null;
  const queryCourseIdRaw = searchParams.get('courseId');
  const queryCourseId = queryCourseIdRaw != null ? Number(queryCourseIdRaw) : null;
  const routeCourseId =
    typeof state?.courseId === 'number'
      ? state.courseId
      : queryCourseId !== null && !Number.isNaN(queryCourseId)
      ? queryCourseId
      : null;

  const [results, setResults] = useState<QuizResultsType | null>(state?.quizResults ?? null);
  const [themeModule, setThemeModule] = useState<ThemeModule | null>(state?.themeModule ?? null);
  const [courseInfo, setCourseInfo] = useState<QuizCourseInfo | null>(null);
  const [flowName, setFlowName] = useState<string | null>(
    state?.flowName ?? state?.quizResults?.attempt?.flow_name ?? null,
  );
  const [isCourseCompleted, setIsCourseCompleted] = useState(false);
  const [showQuestionReview, setShowQuestionReview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasResolved, setHasResolved] = useState(Boolean(state?.quizResults));
  const [flowTheme, setFlowTheme] = useState<string | null>(state?.flowTheme ?? null);
  const [isThemeReady, setIsThemeReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchResults = async () => {
      if (state?.quizResults) {
        setHasResolved(true);
        return;
      }

      if (!flowId) {
        if (isMounted) {
          setError('No results found.');
          setHasResolved(true);
        }
        return;
      }

      try {
        setError(null);
        const response = await quiz.getResults(parseInt(flowId, 10));
        if (!isMounted) {
          return;
        }
        setResults(response.data);
        const resolvedFlowName =
          response.data.attempt?.flow_name ??
          response.data.attempts?.[0]?.flow_name ??
          null;
        if (resolvedFlowName) {
          setFlowName((previous) => previous ?? resolvedFlowName);
        }
      } catch (err) {
        console.error('Error fetching quiz results:', err);
        if (isMounted) {
          setError('Failed to load results.');
        }
      } finally {
        if (isMounted) {
          setHasResolved(true);
        }
      }
    };

    void fetchResults();

    return () => {
      isMounted = false;
    };
  }, [flowId, state?.quizResults]);

  useEffect(() => {
    let isMounted = true;

    const loadCourseInfo = async () => {
      if (!flowName) {
        setCourseInfo(null);
        return;
      }

      try {
        const response = await courses.getFlowByName(flowName, { courseId: routeCourseId });
        if (!isMounted) {
          return;
        }

        const loaded = response.data?.flow;
        if (loaded) setThemeModule({ key: loaded.key, name: loaded.name, title: loaded.title, estimated_duration_minutes: loaded.estimated_duration_minutes, theme_content: loaded.theme_content });
        const loadedTheme = response.data?.flow?.theme;
        if (typeof loadedTheme === 'string' && loadedTheme) {
          setFlowTheme((previous) => previous ?? loadedTheme);
        }

        if (response.data?.course) {
          const nextFlowName =
            response.data?.next_flow && typeof response.data.next_flow.name === 'string'
              ? response.data.next_flow.name
              : null;
          setCourseInfo({
            course: response.data.course,
            flow: response.data.flow,
            nextFlowName,
            nextFlowTitle: response.data?.next_flow?.title || nextFlowName,
            chapterTitle: response.data?.chapter?.title,
          });
          return;
        }

        setCourseInfo(null);
      } catch (err) {
        const data = (err as { response?: { data?: { code?: string; course_id?: number } } })?.response?.data;
        if (isMounted && data?.code === 'course_prerequisites_unmet' && data.course_id) {
          navigate(`/training?courseId=${data.course_id}#courses`, { replace: true, state: { courseLocked: true } });
          return;
        }
        console.warn('Could not load course info for flow', flowName, err);
        if (isMounted) {
          setCourseInfo(null);
        }
      }
    };

    void loadCourseInfo();

    return () => {
      isMounted = false;
    };
  }, [flowName, routeCourseId, navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadCourseCompletion = async () => {
      const courseId = courseInfo?.course?.id;
      const hasPassed = Boolean(results?.passed);
      if (!hasPassed || typeof courseId !== 'number') {
        setIsCourseCompleted(false);
        return;
      }

      try {
        const response = await courses.getCourseCompletion(courseId);
        if (!isMounted) {
          return;
        }
        setIsCourseCompleted(Boolean(response.data?.is_completed));
      } catch (err) {
        console.warn('Could not load course completion status for course', courseId, err);
        if (isMounted) {
          setIsCourseCompleted(false);
        }
      }
    };

    void loadCourseCompletion();

    return () => {
      isMounted = false;
    };
  }, [courseInfo?.course?.id, results?.passed]);

  const handleRetakeQuiz = () => {
    const resolvedFlowName =
      flowName ??
      results?.attempt?.flow_name ??
      results?.attempts?.[0]?.flow_name ??
      null;

    if (resolvedFlowName) {
      navigate(
        buildFlowPath({
          flowName: resolvedFlowName,
          courseId: courseInfo?.course?.id ?? routeCourseId,
        }),
      );
      return;
    }

    navigate('/training#courses');
  };

  const handleContinueCourse = () => {
    if (!flowName) {
      return;
    }

    if (courseInfo?.nextFlowName) {
      navigate(
        buildFlowPath({
          flowName: courseInfo.nextFlowName,
          courseId: courseInfo?.course?.id ?? routeCourseId,
        }),
      );
      return;
    }

    if (typeof courseInfo?.course?.id === 'number') {
      navigate(
        {
          pathname: '/course_complete',
          search: `?courseId=${courseInfo.course.id}`,
        },
        {
          state: {
            courseId: courseInfo.course.id,
            courseName: courseInfo.course.name,
            completedFlowName: flowName,
            completedAt: results?.attempt?.completed_at ?? new Date().toISOString(),
            fromQuiz: true,
          },
        },
      );
      return;
    }

    navigate('/training#courses');
  };

  const handleViewCourseDetails = () => {
    if (typeof courseInfo?.course?.id !== 'number' || !flowName) {
      handleContinueCourse();
      return;
    }

    navigate(
      {
        pathname: '/course_complete',
        search: `?courseId=${courseInfo.course.id}`,
      },
      {
        state: {
          courseId: courseInfo.course.id,
          courseName: courseInfo.course.name,
          completedFlowName: flowName,
          completedAt: results?.attempt?.completed_at ?? new Date().toISOString(),
          fromQuiz: true,
        },
      },
    );
  };

  const themeTotal = Math.max(state?.themeTotal ?? toRoundedNumber(results?.total_questions, 1), 1);
  // The quiz's theme carries on behind the results of a finished attempt: it
  // arrives one step short of the end (taking over from the quiz without an
  // entrance), then completes once its first frame is up, so the finale plays.
  const themeProgress: FlowThemeProgress | null = flowTheme && results?.attempt
    ? {
      step: isThemeReady ? themeTotal : themeTotal - 1,
      total: themeTotal,
      complete: isThemeReady,
    }
    : null;
  const completionEvents = useThemeCompletionEvents(
    state?.themeCompletionId, Boolean(results?.attempt?.completed_at),
    themeModule?.key || themeModule?.name || flowName || '',
    Boolean(results?.passed && themeModule?.theme_content && !themeModule.theme_content.currentModuleCompleted),
  );
  const themeInput = useMemo<ThemeContextInput>(() => {
    const content = themeModule?.theme_content;
    const moduleKey = themeModule?.key || themeModule?.name || flowName || '';
    const finished = Boolean(results?.attempt?.completed_at);
    // Course completion eligibility remains app-owned. Failed quiz attempts do
    // not invent a completed course checkpoint or expose assessment scores.
    const earnedCompletion = finished && results?.passed && content && !content.currentModuleCompleted ? 1 : 0;
    return {
      identity: { firstName: user?.first_name, locale: document.documentElement.lang || navigator.language },
      content: { images: content?.images ?? [], course: content?.course ?? null, chapter: content?.chapter ?? null, module: { key: moduleKey, title: themeModule?.title || themeModule?.name || flowName || '', number: content?.moduleNumber ?? null, total: content?.moduleTotal ?? null, estimatedMinutes: themeModule?.estimated_duration_minutes ?? null }, activity: null },
      progress: { position: finished ? themeTotal : 0, total: themeTotal, completed: finished ? themeTotal : 0, furthest: finished ? themeTotal : 0, complete: finished, visit: state?.quizResults ? 'starting' : 'revisiting', completedModules: content ? content.completedModules + earnedCompletion : null, courseFraction: content ? Math.min(1, content.courseProgress + earnedCompletion / Math.max(1, content.moduleTotal)) : null },
      journey: content?.journey.map(node => earnedCompletion && node.key === moduleKey ? { ...node, state: 'completed' as const } : node) ?? [],
      state: { screen: error ? 'error' : !hasResolved ? 'loading' : finished ? 'completion' : 'review', loading: !hasResolved },
      visualSeed: `${visualSeed}:${moduleKey}`,
      events: completionEvents,
    };
  }, [themeModule, flowName, results?.attempt?.completed_at, results?.passed, user?.first_name, themeTotal, state?.quizResults, error, hasResolved, visualSeed, completionEvents]);
  const dynamicThemeContext = useDynamicThemeContext(themeInput);
  const backToThemeCourse = useCallback(() => {
    if (routeCourseId !== null) navigate(`/training?courseId=${routeCourseId}#courses`);
  }, [routeCourseId, navigate]);
  const isThemed = Boolean(themeProgress);
  useFlowThemeTransitionReady(hasResolved, { unthemed: !isThemed });
  const themeDefinition = resolveFlowTheme(flowTheme);
  // Every branch renders through this, so the theme layer keeps its place in
  // the tree (and its scene) as the page moves between states.
  const renderThemed = (content: React.ReactNode) => (
    <ThemeContextProvider value={dynamicThemeContext}>
      <FlowThemeLayer themeContext={dynamicThemeContext} onBackToCourse={routeCourseId !== null ? backToThemeCourse : undefined} theme={flowTheme} progress={themeProgress} onReadyChange={setIsThemeReady} />
      {isThemed ? <FlowThemeNavBand /> : null}
      <div
        className="flow-theme-scope"
        data-testid="quiz-results-theme-scope"
        data-flow-stage-active="true"
        data-flow-theme={isThemed ? themeDefinition.id : undefined}
        data-flow-card-glass={isThemed ? themeDefinition.cardGlass : undefined}
      >
        {content}
      </div>
    </ThemeContextProvider>
  );

  if (!hasResolved && !results && !error) {
    return renderThemed(
      <div className="mx-auto w-full max-w-xl px-6 py-10 text-foreground">
        <h1 className="text-3xl font-semibold text-center">Results</h1>
      </div>,
    );
  }

  if (error || !results) {
    return renderThemed(
      <div className="mx-auto w-full max-w-xl space-y-4 px-6 py-12 text-center">
        <CardTitle title='Results' size='small' centered/>
        <p className="text-sm text-destructive">{error || 'No results available.'}</p>
        <Button
          onClick={() => navigate('/home')}
          size="auto"
        >
          <Home className="h-4 w-4" />
          Back to Home
        </Button>
      </div>
    );
  }

  const isPassOnly = isPassOnlyResults(results);
  const assessmentLabel = isPassOnly ? 'Survey' : 'Quiz';
  const attempts = Array.isArray(results.attempts) ? results.attempts : [];

  if (!results.attempt) {
    const isNoAttempts = attempts.length === 0;

    return renderThemed(
      <div className="mx-auto w-full max-w-xl space-y-6 px-6 py-12 text-center text-foreground">
        <Card maxWidth="none" variant={cardVariant} data-flow-surface="card">
          <CardTitle
            centered
            title={isNoAttempts ? `No ${assessmentLabel} Attempts` : `No Completed ${assessmentLabel} Attempts`}
            subtitle={
              isNoAttempts
                ? `You haven't started this ${assessmentLabel.toLowerCase()} yet.`
                : `You have started the ${assessmentLabel.toLowerCase()} but haven't completed an attempt yet.`
            }
          />
          <CardContent className="space-y-4">
            <div className="flex flex-row items-center justify-center gap-3">
              <Button
                onClick={handleRetakeQuiz}
                size="auto"
              >
                <RefreshCcw className="h-4 w-4" />
                {isNoAttempts
                  ? `Start ${assessmentLabel}`
                  : `Continue ${assessmentLabel}`}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/home')}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasMaxAttemptsMessage =
    typeof results.message === 'string' &&
    results.message.toLowerCase().includes('max attempts');
  const hasReachedAttemptsLimit =
    hasMaxAttemptsMessage ||
    (results.can_retake === false &&
      typeof results.max_attempts === 'number' &&
      typeof results.attempts_used === 'number' &&
      results.attempts_used >= results.max_attempts &&
      results.passed === false);

  const scorePercentage = toRoundedNumber(results.score_percentage, 0);
  const passingScore = toRoundedNumber(results.passing_score, 70);
  const passed = results.passed ?? false;
  const showCourseCompletedCard = passed && isCourseCompleted;
  const showTopActionRow = (passed && !showCourseCompletedCard) || Boolean(results.can_retake);
  const questionResults = Array.isArray(results.question_results) ? results.question_results : [];
  const canShowQuestionDetails =
    questionResults.length > 0 &&
    (isPassOnly || Boolean(results.question_results_available) || hasReachedAttemptsLimit);

  return renderThemed(
    <div className="mx-auto w-full max-w-xl flex flex-col gap-6 sm:px-6 sm:py-10 text-foreground">
      {/* Over a theme, the heading sits on frosted glass like the cards below it. */}
      <div
        className={cn('text-center', isThemed && 'rounded-3xl px-6 py-4')}
        data-flow-surface={isThemed ? 'card' : undefined}
        data-testid="quiz-results-heading"
      >
        <h1 className="text-3xl font-semibold">{isPassOnly ? 'Survey Recap' : `${assessmentLabel} Results`}</h1>
        {flowName ? (
          <p className="mt-1 text-md text-muted-foreground">
            {courseInfo?.flow?.title || flowName}
          </p>
        ) : null}
        {courseInfo?.course?.name ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {courseInfo.chapterTitle ? `${courseInfo.chapterTitle} · ` : 'Part of '}<span className="font-semibold text-foreground">{courseInfo.course.name}</span>
          </p>
        ) : null}
      </div>

      {!isPassOnly ? (
        <Card maxWidth="none" variant={cardVariant} data-flow-surface="card">
          <CardHeader>
            <CardTitle title="Your Score" size="small" />
          </CardHeader>
          <CardContent className="space-y-4 py-8 text-center">
            <div
              className={cn(
                'mx-auto flex h-28 w-28 items-center justify-center rounded-full border-4',
                passed ? 'border-emerald-400 bg-emerald-500/10' : 'border-rose-400 bg-rose-500/10',
              )}
            >
              <div className="flex flex-col items-center justify-center">
                <span className="text-3xl font-bold">{scorePercentage}%</span>
                <span className="text-xs font-semibold">{passed ? 'Passed' : 'Not Passed'}</span>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                You answered{' '}
                <span className="font-semibold text-foreground">
                  {results.correct_answers ?? 0} of {results.total_questions ?? 0}
                </span>{' '}
                questions correctly.
              </p>
              <p className="text-sm text-muted-foreground">Passing score: {passingScore}%</p>
            </div>
            <Progress
              value={scorePercentage}
              className={cn(
                passed
                  ? 'bg-emerald-500/20 [&>div]:bg-emerald-500'
                  : 'bg-rose-500/20 [&>div]:bg-rose-500',
              )}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card maxWidth="none" variant={cardVariant} data-flow-surface="card">
        <CardHeader>
          <CardTitle title="Attempt Summary" size="small" />
        </CardHeader>
        <CardContent className="space-y-3">
          {!isPassOnly ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-primary/20 text-black">Attempt {results.attempt.attempt_number ?? 1}</Badge>
              <Badge variant="outline" className="text-black bg-white">
                {results.attempts_used ?? 0} of {results.max_attempts ?? 'infinity'} attempts used
              </Badge>
            </div>
          ) : null}
          {formatTimestamp(results.attempt.started_at) ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Started:</span>{' '}
              <span className="font-medium text-foreground">{formatTimestamp(results.attempt.started_at)}</span>
            </p>
          ) : null}
          {formatTimestamp(results.attempt.completed_at) ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Completed:</span>{' '}
              <span className="font-medium text-foreground">{formatTimestamp(results.attempt.completed_at)}</span>
            </p>
          ) : null}
        </CardContent>
      </Card>

      {canShowQuestionDetails ? (
        <Card maxWidth="none" variant={cardVariant} data-flow-surface="card">
          <CardHeader>
            <CardTitle title={isPassOnly ? 'Response Recap' : 'Question Details'} size="small" />
          </CardHeader>
          <CardContent variant="custom" className="space-y-4">
            <div className="flex w-full items-start">
              <Button
                variant="ghost"
                size="auto"
                onClick={() => setShowQuestionReview((previous) => !previous)}
                className="hover:bg-gray-300/40 px-3 py-1"
              >
                {showQuestionReview ? (
                  <>
                    {isPassOnly ? 'Hide Responses' : 'Hide Review'} <ChevronUp className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    {isPassOnly ? 'Show Responses' : 'Show Review'} <ChevronDown className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
            {showQuestionReview ? (
              <div className="space-y-3">
                {questionResults.map((questionResult, index) => (
                  <div
                    key={questionResult.question_id}
                    className={cn(
                      'rounded-2xl border p-3 text-left',
                      isPassOnly
                        ? 'border-border/60 bg-card'
                        : questionResult.is_correct
                        ? 'border-emerald-500/30 bg-emerald-500/10'
                        : 'border-rose-500/30 bg-rose-500/10',
                    )}
                  >
                    {isPassOnly ? (
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <span>Question {index + 1}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        {questionResult.is_correct ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <XCircle className="h-4 w-4 text-rose-400" />
                        )}
                        <span>Question {index + 1}</span>
                        <Badge className={cn('text-black', questionResult.is_correct ? 'bg-emerald-500/80' : 'bg-rose-500/80')}>
                          {questionResult.is_correct ? 'Correct' : 'Incorrect'}
                        </Badge>
                      </div>
                    )}
                    <div className="mt-2 text-sm text-foreground">
                      <QuestionText text={questionResult.question_text} citations={questionResult.question_citations} answerReferences />
                    </div>
                    <CitationList citations={questionResult.question_citations} usedIndices={extractQuestionCitationIndices([questionResult.question_text])} className="px-0 pb-2" />
                    <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                      <p>
                        <span className="font-semibold text-foreground">Your answer:</span>{' '}
                        {formatAnswerValue(questionResult.user_answer)}
                      </p>
                      {!isPassOnly && questionResult.is_correct === false ? (
                        <p>
                          <span className="font-semibold text-foreground">Correct answer:</span>{' '}
                          {formatAnswerValue(questionResult.correct_answer)}
                        </p>
                      ) : null}
                      {questionResult.explanation ? (
                        <p className="italic">
                          <span className="font-semibold text-foreground">Explanation:</span>{' '}
                          {questionResult.explanation}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {showCourseCompletedCard ? (
        <Card
          className={cn('border border-emerald-300 bg-emerald-50/60', isThemed && 'bg-emerald-50/90 backdrop-blur-xl')}
          maxWidth="none"
        >
          <CardHeader className="flex-row items-start gap-3 pb-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
              <Trophy className="size-5 text-emerald-700" />
            </div>
            <div className="flex flex-col gap-1">
              <CardTitle className="text-emerald-900" title="Course Completed" size='small'/>
              <CardDescription className="text-emerald-800/80">
                Congratulations! You have successfully {isPassOnly ? 'completed the survey and completed' : 'passed the quiz and completed'}
                {courseInfo?.course?.name ? (
                  <> <span className="font-medium">{courseInfo.course.name}</span></>
                ) : (
                  ' this course'
                )}
                .
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-100/60 px-4 py-3">
              <p className="text-sm leading-relaxed text-emerald-900">
                Great work! All lessons and assessments are now complete.
                You can review your course details at any time.
              </p>
            </div>

            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button onClick={handleViewCourseDetails} size="auto" className="bg-emerald-600 text-white hover:bg-emerald-700">
                <BookOpen className="mr-2" />
                View Course Summary
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {showTopActionRow ? (
        <div
          className={cn('flex flex-wrap justify-center gap-3', isThemed && 'mx-auto rounded-3xl p-2')}
          data-flow-surface={isThemed ? 'card' : undefined}
          data-testid="quiz-results-actions"
        >
          {passed ? (
            <Button
              onClick={handleContinueCourse}
              size="auto"
            >
              <ArrowRight className="h-4 w-4" />
              {courseInfo?.nextFlowTitle ? `Next: ${courseInfo.nextFlowTitle}` : 'Continue Course'}
            </Button>
          ) : null}
          {results.can_retake ? (
            <Button
              variant="ghost"
              onClick={handleRetakeQuiz}
              size="auto"
            >
              <RefreshCcw className="h-4 w-4" />
              {`Retake ${assessmentLabel}`}
            </Button>
          ) : null}
        </div>
      ) : null}

      {hasReachedAttemptsLimit ? (
        <Card
          maxWidth="none"
          className={cn('border border-amber-300 bg-amber-50/60', isThemed && 'bg-amber-50/90 backdrop-blur-xl')}
        >
          <CardHeader className="flex-row items-center gap-3 pb-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <ShieldAlert className="size-5 text-amber-700" />
            </div>
            <div className="flex flex-col gap-1">
              <CardTitle className="text-amber-900" title="Maximum Attempts Reached" />
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <CardDescription className='text-amber-800/80 font-semibold'>
                {isPassOnly
                  ? 'You have used the available attempt for this survey.'
                  : 'You have used all available attempts for this quiz and did not achieve a passing score.'}
              </CardDescription>
            <div className="rounded-lg border border-amber-200 bg-amber-100/60 px-4 py-3">
              <p className="text-sm leading-relaxed text-amber-900">
                {isPassOnly
                  ? 'Please contact your administrator if this survey needs to be reopened.'
                  : 'Please contact your administrator to request additional attempts or discuss next steps.'}
                <span>
                  {" "}Otherwise, contact{" "}
                <a
                  href={`mailto:${supportEmail}`}
                  className="font-medium hover:underline underline-offset-2 hover:text-amber-700"
                  onClick={() => {
                    track('button_click', {
                      action: 'support_cta_click',
                      control_id: 'quiz_results_support_email',
                      support_target: 'email',
                      support_surface: 'quiz_results',
                      destination: `mailto:${supportEmail}`,
                    });
                  }}
                >
                  {supportEmail}
                </a>
                .
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {hasReachedAttemptsLimit ? (
        <div className="flex justify-center">
          <Button
            size="auto"
            onClick={() => navigate('/home')}
          >
            <Home className="h-4 w-4" />
            Back to Home
          </Button>
        </div>
      ) : null}

    </div>
  );
};

export default QuizResults;
